import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import ConfirmModal from './ConfirmModal';
import EventDetailsModal from './EventDetailsModal'; // Importáljuk az új modális ablakot
import './Appointments.css';
import { apiRequest } from '../../utils'; // apiRequest importálása
import translations from '../../lang';

// Function to generate color based on service ID or name
const generateColor = (identifier, hasBookings) => {
  const hash = Array.from(identifier).reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const hue = hash % 360;

  if (hasBookings) {
    return `hsl(${hue}, 70%, 50%)`; // Erősebb szín a foglalt időpontokhoz
  } else {
    return `hsl(${hue}, 50%, 80%)`; // Halványabb árnyalat a nem foglalt időpontokhoz
  }
};

const language = localStorage.getItem('language') || 'ro';
const t = translations[language];

const Appointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [currentService, setCurrentService] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [timeSlot, setTimeSlot] = useState('00:30'); // Default slot interval (30 minutes)
  const [detailsModalOpen, setDetailsModalOpen] = useState(false); // Új modális állapot
  const [capacity, setCapacity] = useState('1'); // Férőhelyek száma

  // Esemény részleteinek megnyitása
  const handleEventClick = (clickInfo) => {
    const eventDetails = {
      id: clickInfo.event.id,
      title: clickInfo.event.title,
      start: clickInfo.event.start,
      end: clickInfo.event.end,
      bookings: clickInfo.event.extendedProps.bookings || [],
      service_id: clickInfo.event.extendedProps.service_id || clickInfo.event.extendedProps.service?.id
    };    

    setCurrentEvent(eventDetails);
    setDetailsModalOpen(true); // Megnyitjuk az esemény részleteit tartalmazó modált
  };

  // Törlés funkció
  const handleDelete = () => {
    apiRequest(`${process.env.REACT_APP_API}/appointments/${currentEvent.id}/`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Hiba az időpont törlése során');
        }
        fetchAppointments(); // Frissítjük az időpontokat
        setDetailsModalOpen(false); // Bezárjuk az esemény részleteit tartalmazó modált a törlés után
      })
      .catch(error => console.error('Hiba az időpont törlésekor:', error));
  };

  // Áthelyezés funkció
  const handleMoveAppointment = ({ new_slot_id, current_appointment_id }) => {
    apiRequest(`${process.env.REACT_APP_API}/api/appointments/move/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': language, // <== itt küldjük a nyelvet
      },
      body: JSON.stringify({ 
        new_slot_id,
        current_appointment_id
      }),
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        fetchAppointments();
        setDetailsModalOpen(false); // Bezárjuk a modált áthelyezés után
      } else {
        alert(data.error || t.move_failed);
      }
    })
    .catch(error => console.error('Hiba az áthelyezés során:', error));
  };

  // Move booking handler
  const handleMoveBooking = ({ new_slot_id, booking_id }) => {
      apiRequest(`${process.env.REACT_APP_API}/api/booking-move/`, {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
              'Accept-Language': language, // <== itt küldjük a nyelvet
          },
          body: JSON.stringify({ new_slot_id, booking_id }),
      })
      .then(response => response.json())
      .then(data => {
          if (data.success) {
              fetchAppointments();
              setDetailsModalOpen(false);
          } else {
              alert(data.error || 'Nem sikerült az áthelyezés.');
          }
      })
      .catch(error => console.error('Hiba az áthelyezés során:', error));
  };

  // Booking handler by name
    const handleBooking = (name) => {
        const appointmentId = currentEvent.id;
        apiRequest(`${process.env.REACT_APP_API}/api/appointments/${appointmentId}/book-by-name/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept-Language': language, // <== itt küldjük a nyelvet
            },
            body: JSON.stringify({ booked_by_name: name }),
        })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
              fetchAppointments();
              setDetailsModalOpen(false);
            } else {
              alert(data.error || t.booking_failed);
            }
        })
        .catch(error => console.error('Hiba a foglalás során:', error));
    };

    // Booking deletion handler
    const handleDeleteBooking = (bookingId) => {
        apiRequest(`${process.env.REACT_APP_API}/api/bookings/${bookingId}/delete/`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                'Accept-Language': language, // <== itt küldjük a nyelvet
            },
        })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                setCurrentEvent(prevDetails => ({
                    ...prevDetails,
                    bookings: prevDetails.bookings.filter(booking => booking.id !== bookingId),
                }));
                fetchAppointments(); // Refresh appointments
            } else {
              alert(data.error || t.delete_booking_failed);
            }
        })
        .catch(error => console.error('Hiba a foglalás törlése során:', error));
    };

  // Naptár beállítások
  const calendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    initialView: 'timeGridWeek',
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'timeGridWeek,timeGridDay',
    },
    views: {
      timeGridWeek: {
        dayHeaderFormat: { weekday: 'short', day: 'numeric', separator: ' ' } // Vessző nélküli elválasztás
      }
    },    
    events: appointments,
    selectable: true,
    selectMirror: true,
    dayMaxEvents: true,
    slotDuration: timeSlot,
    slotLabelInterval: timeSlot,
    slotMinTime: '06:00:00',
    slotMaxTime: '24:00:00',
    allDaySlot: false,
    locale: language,
    buttonText: {
      today: t.today,
      month: t.month,
      week: t.week,
      day: t.day,
    },    
    firstDay: 1,
    height: 'auto',
    slotMinWidth: 50,
    select: handleDateSelect,
    eventClick: handleEventClick, // A click handler helyesen hivatkozva  
  };

  useEffect(() => {
    fetchAppointments();
    fetchUserServices();
  }, []);

  // Időpontok lekérése
  const fetchAppointments = () => {
    const token = localStorage.getItem('access_token');

    apiRequest(`${process.env.REACT_APP_API}/appointments/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Hiba az időpontok lekérdezése során');
        }
        return response.json();
      })
      .then(data => {
        const formattedAppointments = data.map(event => {
          const bookedCount = event.bookings ? event.bookings.length : 0;
          const maxCapacity = event.max_capacity || 1;

          const statuses = event.bookings.map(booking => booking.status);
          let statusSymbol = '';

          if (statuses.length === 0) {
              statusSymbol = ''; // Ha nincs foglalás, nincs jel
          } 
          
          if (statuses.includes('Confirmed') || statuses.includes('confirmed')) {
              statusSymbol += '✅'; // Ha van "Confirmed", pipa
          }
          
          if (statuses.includes('Rejected') || statuses.includes('rejected')) {
              statusSymbol += '❌'; // Ha van "Rejected", X
          }
          
          if (statuses.includes('Pending') || statuses.includes('pending')) {
              statusSymbol += '⚠️'; // Ha van "Pending", felkiáltójel
          }

          return {
            id: event.id,
            title: `${event.service_name || 'Névtelen szolgáltatás'} - ${bookedCount}/${maxCapacity} ${statusSymbol}`,
            start: new Date(event.start_time),
            end: new Date(event.end_time),
            bookings: event.bookings || [],
            color: generateColor(event.service_name, event.bookings.length > 0), // Színezés érintetlen
            service_id: event.service, // <- EZT ADD HOZZÁ
          };
        });
        setAppointments(formattedAppointments);
      })
      .catch(error => console.error('Hiba az időpontok lekérdezésekor:', error));
  };

  // Szolgáltatások lekérése
  const fetchUserServices = () => {
    apiRequest(`${process.env.REACT_APP_API}/user-services2/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': language, // <== itt küldjük a nyelvet
      },
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Hiba a szolgáltatások lekérdezése során');
        }
        return response.json();
      })
      .then(data => {
        if (Array.isArray(data.data)) {
          setServices(data.data);
          if (data.data.length > 0) {
            const firstService = data.data[0];
            setCurrentService(firstService.id.toString());
            setTimeSlot(firstService.interval);
            setCapacity(firstService.max_capacity);
          }
        } else {
          console.error('Vártunk egy tömböt, de kaptunk:', data.data);
          setServices([]);
        }
      })
      .catch(error => console.error('Hiba a szolgáltatások lekérdezésekor:', error));
  };

  // Intervallum frissítése és elmentése a backendre
  const handleIntervalChange = (e) => {
    const selectedInterval = e.target.value;
    setTimeSlot(selectedInterval);

    if (currentService) {
      const selectedServiceId = parseInt(currentService);
      apiRequest(`${process.env.REACT_APP_API}/user-services/${selectedServiceId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': language, // <== itt küldjük a nyelvet
        },
        body: JSON.stringify({ interval: selectedInterval }),
      })
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to update service interval');
          }
          return response.json();
        })
        .then(data => {
          console.log('Service interval updated', data);
        })
        .catch(error => console.error('Error updating service interval:', error));
    }
  };

  // Kapacitás kezelése
  const handleCapacityChange = (e) => {
    const selectedCapacity = e.target.value;
    setCapacity(selectedCapacity);

    if (currentService) {
      const selectedServiceId = parseInt(currentService);
      apiRequest(`${process.env.REACT_APP_API}/user-services/${selectedServiceId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': language, // <== itt küldjük a nyelvet
        },
        body: JSON.stringify({ max_capacity: selectedCapacity }),
      })
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to update service capacity');
          }
          return response.json();
        })
        .then(data => {
          console.log('Service capacity updated', data);
        })
        .catch(error => console.error('Error updating service capacity:', error));
    }
  };

  // Esemény kiválasztása és mentése az adatbázisba
  function handleDateSelect(selectInfo) {
    if (!currentService) {
      alert(t.select_service_alert);
      return;
    }

    const token = localStorage.getItem('access_token');
    let userId = null;

    if (token) {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      const payload = JSON.parse(jsonPayload);
      userId = payload.user_id;
    }

    if (!userId) {
      alert(t.login_error);
      return;
    }

    const selectedServiceId = parseInt(currentService);
    const maxCapacity = parseInt(capacity);

    const eventData = {
      start_time: selectInfo.startStr,
      end_time: selectInfo.endStr,
      service: selectedServiceId,
      user: userId,
      max_capacity: maxCapacity,
    };

    apiRequest(`${process.env.REACT_APP_API}/appointments/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
          'Accept-Language': language, // <== itt küldjük a nyelvet
      },
      body: JSON.stringify(eventData),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to save the event');
        }
        return response.json();
      })
      .then(data => {
        const serviceName = services.find(service => service.id === selectedServiceId)?.name || 'Névtelen szolgáltatás';
        const title = `${serviceName} - 0/${maxCapacity} foglalás`;

        setAppointments(prevAppointments => [...prevAppointments, {
          ...data,
          start: new Date(data.start_time),
          end: new Date(data.end_time),
          title,
          color: data.booked_by ? 'red' : '',
        }]);
        fetchAppointments(); // Frissítjük az időpontokat
      })
      .catch(error => console.error('Hiba az időpont mentésekor:', error));
  }

  function closeConfirmModal() {
    setModalOpen(false);
  }

  return (
    <div className="appointments-container">
      <h1 className="appointments-title">{t.appointments}</h1>

      <div className="form-select-group">
        <div className="service-select">
        <label>{t.select_service}</label>
          <select onChange={e => setCurrentService(e.target.value)} value={currentService}>
            {services.length === 0 ? (
              <option value="">{t.choose_service}</option>
            ) : (
              services.map(service => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))
            )}
          </select>
        </div>

        <div className="capacity-select">
        <label>{t.capacity}</label>
          <select onChange={handleCapacityChange} value={capacity}>
            {[...Array(20).keys()].map(i => (
              <option key={i + 1} value={i + 1}>{i + 1} {t.people}</option>
            ))}
          </select>
        </div>

        <div className="slot-select">
        <label>{t.select_interval}</label>
          <select onChange={handleIntervalChange} value={timeSlot}>
            <option value="00:15">{t.minutes_15}</option>
            <option value="00:20">{t.minutes_20}</option>
            <option value="00:30">{t.minutes_30}</option>
            <option value="01:00">{t.minutes_60}</option>
          </select>
        </div>
      </div>

      <FullCalendar {...calendarOptions} />

      {/* Esemény részletek modális */}
      <EventDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        eventDetails={currentEvent}
        onDelete={handleDelete}
        onMoveBooking={handleMoveBooking}
        onMoveAppointment={handleMoveAppointment}
        onBook={handleBooking}
        onDeleteBooking={handleDeleteBooking}
        fetchAppointments={fetchAppointments} // Adj hozzá ezt a sort
      />

      {/* Törlés megerősítő modális */}
      <ConfirmModal
        isOpen={modalOpen}
        onClose={closeConfirmModal}
        onConfirm={handleDelete}
        message={`${t.confirm_delete} '${currentEvent?.title}'?`}
      />
    </div>
  );
};

export default Appointments;
